import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert,
  TableSortLabel,
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { useApiClient } from '../utils/apiClient';

// Register Chart.js components at module level
// This ensures registration happens before any component renders
// For mixed charts (bar + line), we must register both controllers and elements
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const DailyLabelsReport = () => {
  const apiClient = useApiClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [dateRange, setDateRange] = useState('30d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [orderBy, setOrderBy] = useState('date');
  const [order, setOrder] = useState('desc'); // 'asc' or 'desc'

  const getDateRange = () => {
    const now = new Date();
    let startDate, endDate;

    // Set endDate to end of today (23:59:59.999) to ensure we include all of today's data
    const endOfToday = new Date(now);
    endOfToday.setHours(23, 59, 59, 999);

    switch (dateRange) {
      case '7d':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        // Set start of the start date to beginning of day
        startDate.setHours(0, 0, 0, 0);
        endDate = endOfToday;
        break;
      case '30d':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        startDate.setHours(0, 0, 0, 0);
        endDate = endOfToday;
        break;
      case '90d':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        startDate.setHours(0, 0, 0, 0);
        endDate = endOfToday;
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          startDate.setHours(0, 0, 0, 0);
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59, 999);
        } else {
          return null;
        }
        break;
      default:
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        startDate.setHours(0, 0, 0, 0);
        endDate = endOfToday;
    }

    return { startDate, endDate };
  };

  const fetchReport = async () => {
    const range = getDateRange();
    if (!range) {
      setError('Please select both start and end dates for custom range');
      return;
    }

    // Debug logging
    console.log('📊 Daily Labels Report - Date Range:', {
      startDate: range.startDate.toISOString(),
      endDate: range.endDate.toISOString(),
      startDateLocal: range.startDate.toLocaleString(),
      endDateLocal: range.endDate.toLocaleString(),
      now: new Date().toISOString(),
      nowLocal: new Date().toLocaleString(),
    });

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/analytics/reports/daily-labels', {
        params: {
          startDate: range.startDate.toISOString(),
          endDate: range.endDate.toISOString(),
        },
      });
      
      console.log('📊 Daily Labels Report - Response:', {
        dataCount: response.data?.length || 0,
        dates: response.data?.map(d => d.date) || [],
        latestDate: response.data?.length > 0 ? response.data[response.data.length - 1]?.date : 'none',
      });

      // Fill in missing dates with zero values
      const fillMissingDates = (data, startDate, endDate) => {
        // Parse date string as local date (YYYY-MM-DD format)
        const parseLocalDate = (dateStr) => {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day);
        };
        
        // Format date as YYYY-MM-DD
        const formatDate = (date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}`;
        };

        // Create a map of existing data by date
        const dataMap = new Map();
        (data || []).forEach((item) => {
          dataMap.set(item.date, item);
        });

        // Generate complete date range
        const completeData = [];
        const currentDate = new Date(startDate);
        currentDate.setHours(0, 0, 0, 0);
        const end = new Date(endDate);
        end.setHours(0, 0, 0, 0);

        while (currentDate <= end) {
          const dateStr = formatDate(currentDate);
          if (dataMap.has(dateStr)) {
            // Use existing data
            completeData.push(dataMap.get(dateStr));
          } else {
            // Fill in missing date with zeros
            completeData.push({
              date: dateStr,
              labelsCreated: 0,
              labelsCanceled: 0,
              successfulCreations: 0,
              failedCreations: 0,
              labelErrors: 0,
            });
          }
          // Move to next day
          currentDate.setDate(currentDate.getDate() + 1);
        }

        return completeData;
      };

      // Fill in missing dates
      const filledData = fillMissingDates(response.data || [], range.startDate, range.endDate);

      // Sort data by date (newest to oldest) by default
      // Parse dates as local dates to avoid timezone shift issues
      const sortedData = filledData.sort((a, b) => {
        // Parse date string as local date (YYYY-MM-DD format)
        const parseLocalDate = (dateStr) => {
          const [year, month, day] = dateStr.split('-').map(Number);
          return new Date(year, month - 1, day);
        };
        return parseLocalDate(b.date) - parseLocalDate(a.date);
      });
      setData(sortedData);
    } catch (err) {
      console.error('Error fetching daily labels report:', err);
      setError(err.response?.data?.message || 'Failed to fetch report data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [dateRange, customStartDate, customEndDate]);

  // Calculate trend lines using linear regression
  const calculateTrend = (values) => {
    const n = values.length;
    if (n < 2) return values;

    let sumX = 0;
    let sumY = 0;
    let sumXY = 0;
    let sumX2 = 0;

    values.forEach((y, i) => {
      const x = i;
      sumX += x;
      sumY += y;
      sumXY += x * y;
      sumX2 += x * x;
    });

    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;

    return values.map((_, i) => slope * i + intercept);
  };

  // Prepare chart data
  const labels = data.map((d) => {
    // Parse date string as local date to avoid timezone shift
    const parseLocalDate = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };
    const date = parseLocalDate(d.date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });

  const chartData = {
    labels,
    datasets: [
      {
        type: 'bar',
        label: 'Labels Created',
        data: data.map((d) => d.labelsCreated),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
        order: 2,
      },
      {
        type: 'bar',
        label: 'Labels Canceled',
        data: data.map((d) => d.labelsCanceled),
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
        order: 2,
      },
      {
        type: 'bar',
        label: 'Label Errors',
        data: data.map((d) => d.failedCreations),
        backgroundColor: 'rgba(255, 159, 64, 0.6)',
        borderColor: 'rgba(255, 159, 64, 1)',
        borderWidth: 1,
        order: 2,
      },
      {
        type: 'line',
        label: 'Created Trend',
        data: calculateTrend(data.map((d) => d.labelsCreated)),
        borderColor: 'rgba(54, 162, 235, 0.8)',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
        order: 1,
      },
      {
        type: 'line',
        label: 'Canceled Trend',
        data: calculateTrend(data.map((d) => d.labelsCanceled)),
        borderColor: 'rgba(255, 99, 132, 0.8)',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
        order: 1,
      },
      {
        type: 'line',
        label: 'Errors Trend',
        data: calculateTrend(data.map((d) => d.failedCreations)),
        borderColor: 'rgba(255, 159, 64, 0.8)',
        backgroundColor: 'transparent',
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        pointHoverRadius: 0,
        order: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Daily Labels Created and Canceled',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
  };

  // Handle sorting
  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return num.toLocaleString('en-US');
  };

  // Sort the data based on current sort settings
  const sortedData = [...data].sort((a, b) => {
    let aValue, bValue;

    // Helper to parse date string as local date (YYYY-MM-DD format)
    const parseLocalDate = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day);
    };

    switch (orderBy) {
      case 'date':
        aValue = parseLocalDate(a.date);
        bValue = parseLocalDate(b.date);
        break;
      case 'labelsCreated':
        aValue = a.labelsCreated;
        bValue = b.labelsCreated;
        break;
      case 'successful':
        aValue = a.successfulCreations;
        bValue = b.successfulCreations;
        break;
      case 'failed':
        aValue = a.failedCreations;
        bValue = b.failedCreations;
        break;
      case 'labelsCanceled':
        aValue = a.labelsCanceled;
        bValue = b.labelsCanceled;
        break;
      case 'netLabels':
        aValue = a.labelsCreated - a.labelsCanceled;
        bValue = b.labelsCreated - b.labelsCanceled;
        break;
      default:
        aValue = new Date(a.date);
        bValue = new Date(b.date);
    }

    if (orderBy === 'date') {
      // For dates, compare as Date objects
      return order === 'asc' 
        ? aValue - bValue 
        : bValue - aValue;
    } else {
      // For numbers, compare as numbers
      return order === 'asc' 
        ? aValue - bValue 
        : bValue - aValue;
    }
  });

  const totalCreated = data.reduce((sum, d) => sum + d.labelsCreated, 0);
  const totalCanceled = data.reduce((sum, d) => sum + d.labelsCanceled, 0);
  const totalSuccessful = data.reduce((sum, d) => sum + d.successfulCreations, 0);
  const totalFailed = data.reduce((sum, d) => sum + d.failedCreations, 0);

  return (
    <Box>
      {/* Date Range Selector */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Date Range</InputLabel>
                <Select
                  value={dateRange}
                  onChange={(e) => setDateRange(e.target.value)}
                  label="Date Range"
                >
                  <MenuItem value="7d">Last 7 Days</MenuItem>
                  <MenuItem value="30d">Last 30 Days</MenuItem>
                  <MenuItem value="90d">Last 90 Days</MenuItem>
                  <MenuItem value="custom">Custom Range</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {dateRange === 'custom' && (
              <>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Created
                  </Typography>
                  <Typography variant="h4">{formatNumber(totalCreated)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Total Canceled
                  </Typography>
                  <Typography variant="h4">{formatNumber(totalCanceled)}</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Successful
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {formatNumber(totalSuccessful)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Failed
                  </Typography>
                  <Typography variant="h4" color="error.main">
                    {formatNumber(totalFailed)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Chart */}
          <Card sx={{ mb: 3 }}>
            <CardHeader title="Daily Labels Chart" />
            <CardContent>
              <Box sx={{ height: 400 }}>
                <Chart data={chartData} options={chartOptions} />
              </Box>
            </CardContent>
          </Card>

          {/* Data Table */}
          <Card>
            <CardHeader title="Daily Labels Data" />
            <CardContent>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <TableSortLabel
                          active={orderBy === 'date'}
                          direction={orderBy === 'date' ? order : 'desc'}
                          onClick={() => handleSort('date')}
                        >
                          Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'labelsCreated'}
                          direction={orderBy === 'labelsCreated' ? order : 'desc'}
                          onClick={() => handleSort('labelsCreated')}
                        >
                          Labels Created
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'successful'}
                          direction={orderBy === 'successful' ? order : 'desc'}
                          onClick={() => handleSort('successful')}
                        >
                          Successful
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'failed'}
                          direction={orderBy === 'failed' ? order : 'desc'}
                          onClick={() => handleSort('failed')}
                        >
                          Failed
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'labelsCanceled'}
                          direction={orderBy === 'labelsCanceled' ? order : 'desc'}
                          onClick={() => handleSort('labelsCanceled')}
                        >
                          Labels Canceled
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">
                        <TableSortLabel
                          active={orderBy === 'netLabels'}
                          direction={orderBy === 'netLabels' ? order : 'desc'}
                          onClick={() => handleSort('netLabels')}
                        >
                          Net Labels
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedData.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          <Typography color="textSecondary">
                            No data available for the selected date range
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedData.map((row) => (
                        <TableRow key={row.date}>
                          <TableCell>
                            {(() => {
                              // Parse date string as local date to avoid timezone shift
                              const [year, month, day] = row.date.split('-').map(Number);
                              const date = new Date(year, month - 1, day);
                              return date.toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                              });
                            })()}
                          </TableCell>
                          <TableCell align="right">{formatNumber(row.labelsCreated)}</TableCell>
                          <TableCell align="right" style={{ color: '#4caf50' }}>
                            {formatNumber(row.successfulCreations)}
                          </TableCell>
                          <TableCell align="right" style={{ color: '#f44336' }}>
                            {formatNumber(row.failedCreations)}
                          </TableCell>
                          <TableCell align="right" style={{ color: '#ff9800' }}>
                            {formatNumber(row.labelsCanceled)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(row.labelsCreated - row.labelsCanceled)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default DailyLabelsReport;

